import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Button,
  DatePicker,
  Dropdown,
  Empty,
  Form,
  Input,
  List,
  message,
  Modal,
  Select,
  Skeleton,
  Space,
  Tabs,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import {
  ClockCircleOutlined,
  DeleteOutlined,
  FileOutlined,
  LeftOutlined,
  MessageOutlined,
  MoreOutlined,
  PaperClipOutlined,
  PictureOutlined,
  PlusOutlined,
  RobotOutlined,
  SearchOutlined,
  SendOutlined,
  SmileOutlined,
  UserOutlined,
} from "@ant-design/icons";
import EmojiPicker from "emoji-picker-react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import styled from "styled-components";
import debounce from "lodash/debounce";
import { media } from "../../../../styles/breakpoints";
import { useChatSessions } from "../../../../data/hooks/firestore/useChatSessions";
import { INITIAL_MESSAGE_LIMIT, useChatMessages } from "../../../../data/hooks/firestore/useChatMessages";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useAuth } from "../../../../data/hooks/firebase/useAuth";
import axios from "axios";
import CrossIcon from "../../../../components/shared-components/Icons/CrossIcon";
import { storage } from "../../../../configs/FirebaseConfig";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import useIsMobile from "../../../../hooks/useIsMobile";
import { useWhatsappTemplates } from "../../../../data/hooks/firestore/useWhatsappTemplates";
import Utils from "../../../../utils";
import MessageItem from "./MessageItem";
import VirtualizedChatList from "./VirtualizedChatList";
import ChatListSkeleton from "./ChatListSkeleton";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

const { TabPane } = Tabs;
const { Text, Title } = Typography;
const { Dragger } = Upload;

const Chat = () => {
  const { t } = useTranslation();
  const {
    data: chatSessions,
    loading: chatSessionsLoading,
    updateChatSession,
    saveOutgoingMessage,
    enqueueOutgoingWhatsappMessage,
    cancelOutgoingWhatsappMessage,
    updateChatMessage,
    createChatSession,
    deleteChatSession,
  } = useChatSessions();
  const { data: whatsappTemplates } = useWhatsappTemplates();

  // State declarations must come before hooks that depend on them
  const [messageApi, contextHolder] = message.useMessage();
  const { uid } = useAuth();
  const messagesContainerRef = useRef(null);
  const previousChatIdRef = useRef(null);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedChat, setSelectedChat] = useState(null);
  const [messageLimit, setMessageLimit] = useState(INITIAL_MESSAGE_LIMIT);

  // Reset the message page size whenever a different chat is opened
  useEffect(() => {
    setMessageLimit(INITIAL_MESSAGE_LIMIT);
  }, [selectedChat?.id]);

  // Now we can use selectedChat in this hook
  const {
    data: selectedChatMessages,
    loading: selectedChatMessagesLoading,
    hasMore: hasMoreMessages,
  } = useChatMessages(selectedChat?.id, messageLimit);
  const activeChat = selectedChat ? { ...selectedChat, messages: selectedChatMessages || [] } : null;
  const loadingChat = selectedChatMessagesLoading && !!selectedChat;
  const [messageInput, setMessageInput] = useState("");
  const [autoModeSendWarning, setAutoModeSendWarning] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  // Synchronous guard against double-submit (double click / double Enter before the
  // first send round-trips): a ref updates immediately, unlike React state, so a second
  // call to handleSendMessage in the same tick still sees the lock.
  const sendingMessageRef = useRef(false);
  // searchInput updates immediately (controlled input); searchTerm is the debounced value
  // used for actually filtering, so typing doesn't re-run the filter+sort on every keystroke.
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSetSearchTerm = useMemo(() => debounce((value) => setSearchTerm(value), 200), []);
  useEffect(() => () => debouncedSetSearchTerm.cancel(), [debouncedSetSearchTerm]);
  const handleSearchInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchInput(value);
      debouncedSetSearchTerm(value);
    },
    [debouncedSetSearchTerm],
  );
  const [sendingWhatsappMessageId, setSendingWhatsappMessageId] = useState(null);
  const [imagePreview, setImagePreview] = useState({ open: false, url: "", caption: "" });
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [pendingAttachment, setPendingAttachment] = useState(null); // { file, type: 'image'|'document', previewUrl }
  const [dragActive, setDragActive] = useState(false);
  const isMobile = useIsMobile();

  // Floating date indicator state
  const [floatingDate, setFloatingDate] = useState(null);
  const [showFloatingDate, setShowFloatingDate] = useState(false);
  const floatingDateTimeoutRef = useRef(null);

  // New chat modal state
  const [newChatModalOpen, setNewChatModalOpen] = useState(false);
  const [newChatForm] = Form.useForm();

  // Inline edit contact name state
  const [editingName, setEditingName] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState("");

  // Emoji picker state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);

  // Template sending state
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");
  const [templateForm] = Form.useForm();
  // Watch all template form values to keep preview in sync while typing
  const watchedTemplateValues = Form.useWatch([], templateForm);

  // Scheduled message state
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduledFor, setScheduledFor] = useState(null);
  const [schedulingMessage, setSchedulingMessage] = useState(false);

  // Reaction state
  const [reactionPickerMessageId, setReactionPickerMessageId] = useState(null);
  const [reactionPickerPlacement, setReactionPickerPlacement] = useState("top");
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [sendingReactionMessageId, setSendingReactionMessageId] = useState(null);
  const reactionPickerRef = useRef(null);

  const setPendingFromFile = (file) => {
    if (!file) return;
    // Clean existing preview if present
    if (pendingAttachment?.previewUrl) URL.revokeObjectURL(pendingAttachment.previewUrl);
    const type = file.type && file.type.startsWith("image/") ? "image" : "document";
    const previewUrl = type === "image" ? URL.createObjectURL(file) : null;
    setPendingAttachment({ file, type, previewUrl });
  };

  const handleCancelScheduledMessage = async (chatId, msg) => {
    try {
      await cancelOutgoingWhatsappMessage({
        chatId,
        messageId: msg.id,
        queueDocId: msg.queueDocId,
      });
      await updateChatMessage(chatId, msg.id, {
        isCancelled: true,
        cancelledAt: dayjs().unix(),
      });
      messageApi.success(t("cancel") || "Geannuleerd");
    } catch (e) {
      messageApi.error(e?.message || t("error"));
    }
  };

  const clearPendingAttachment = () => {
    if (pendingAttachment?.previewUrl) URL.revokeObjectURL(pendingAttachment.previewUrl);
    setPendingAttachment(null);
  };

  // Cleanup temporary object URL when pending attachment changes or on unmount
  useEffect(() => {
    return () => {
      if (pendingAttachment?.previewUrl) URL.revokeObjectURL(pendingAttachment.previewUrl);
    };
  }, [pendingAttachment]);

  // Handle pasting an image from the clipboard (used on the message input)
  const handlePasteImage = (e) => {
    try {
      const clipboard = e.clipboardData || window.clipboardData;
      if (!clipboard || !clipboard.items || !clipboard.items.length) return;

      for (const item of clipboard.items) {
        if (item.kind === "file" && item.type && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            // Prevent default paste of the image blob into the input
            e.preventDefault();
            if (uploadingMedia) return;
            setPendingFromFile(file);
            if (!selectedChat) {
              try {
                messageApi.info(t("select_chat_title"));
              } catch (_) {}
            }
          }
          break;
        }
      }
    } catch (_) {
      // no-op
    }
  };

  // Detect global file drag to enable invisible overlay Dragger
  useEffect(() => {
    const onDragEnter = (e) => {
      const hasFiles = Array.from(e?.dataTransfer?.types || []).includes("Files");
      if (hasFiles) setDragActive(true);
    };
    const onDragOver = (e) => {
      if (dragActive) e.preventDefault();
    };
    const onDragLeave = (e) => {
      // If leaving the window, deactivate
      if (
        e.clientX <= 0 ||
        e.clientY <= 0 ||
        (window && (e.clientX >= window.innerWidth || e.clientY >= window.innerHeight))
      ) {
        setDragActive(false);
      }
    };
    const onDrop = (e) => {
      e.preventDefault();
      setDragActive(false);
    };

    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, [dragActive]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showEmojiPicker]);

  // Close reaction picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (reactionPickerRef.current && !reactionPickerRef.current.contains(event.target)) {
        setReactionPickerMessageId(null);
      }
    };

    if (reactionPickerMessageId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [reactionPickerMessageId]);

  // Mobile back gesture support: when a chat is opened on mobile, push state; on popstate, go back to list
  useEffect(() => {
    const onPopState = () => {
      if (isMobile) {
        // Return to list view
        setSelectedChat(null);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [isMobile]);

  const handleFileDownload = (url, filename) => {
    try {
      const link = document.createElement("a");
      link.href = url;
      if (filename) link.download = filename;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      window.open(url, "_blank");
    }
  };

  const uploadFileToStorage = async (file, chatId) => {
    const ts = dayjs().unix();
    const safeName = encodeURIComponent(file.name || `media-${ts}`);
    const path = `whatsapp/${chatId}/${ts}-${safeName}`;
    const ref = storageRef(storage, path);
    await uploadBytes(ref, file, { contentType: file.type });
    const url = await getDownloadURL(ref);
    return { url, path };
  };

  const handleSendMedia = async (file, type, options = {}) => {
    if (!activeChat || !file) return;
    try {
      setUploadingMedia(true);
      const { url } = await uploadFileToStorage(file, activeChat.id);

      const timestamp = dayjs().unix();
      const messageNumber = activeChat.messages.length + 1;
      const newMessage = {
        createdAt: timestamp,
        type: "outgoing",
        messageBody: options.caption != null ? options.caption : "",
        messageType: type, // "image" | "document"
        senderId: uid,
        messageNumber,
        fileDownloadUrl: url,
        fileName: file.name,
        timestamps: {
          whatsappSent: null,
          whatsappDelivered: null,
          whatsappRead: null,
        },
      };

      const messageId = await saveOutgoingMessage(activeChat.id, newMessage);
      await updateChatSession(activeChat.id, {
        updatedAt: timestamp,
        messageCount: activeChat.messageCount + 1,
      });

      scrollChatToBottom();
      await sendWhatsappMessage(newMessage, activeChat.id, messageId);
    } catch (err) {
      console.error(err);
      messageApi.error(err.message || "Failed to send media");
    } finally {
      setUploadingMedia(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (options.clearCaption) setMessageInput("");
    }
  };

  // Keyboard navigation: Tab to switch between chats
  useEffect(() => {
    const onKeyDown = (e) => {
      if (templateModalOpen || newChatModalOpen) return;
      if (e.key !== "Tab") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return; // ignore combinations

      const filtered = getFilteredChats();
      if (!filtered.length) return;

      // Determine current index in filtered list
      const currentIndex = selectedChat ? filtered.findIndex((c) => c.id === selectedChat.id) : -1;

      // Compute next index (wrap around)
      let nextIndex;
      if (e.shiftKey) {
        nextIndex = currentIndex <= 0 ? filtered.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex >= filtered.length - 1 ? 0 : currentIndex + 1;
      }

      // Prevent default focus movement and select chat
      e.preventDefault();
      const nextChat = filtered[nextIndex];
      if (nextChat) {
        handleChatSelect(nextChat);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // Dependencies: ensure we react to changes in selection and filters
  }, [activeTab, searchTerm, chatSessions, selectedChat, templateModalOpen, newChatModalOpen]);

  // Update selected chat if changed from outside and handle scrolling behavior
  useEffect(() => {
    if (!chatSessions?.length || !selectedChat) return;

    const isNewSelection = previousChatIdRef.current !== selectedChat.id;
    previousChatIdRef.current = selectedChat.id;

    // On opening a chat: jump to bottom and ensure it settles at the true bottom (handles tall/late-loading items)
    if (isNewSelection) {
      setTimeout(() => {
        scrollChatToBottomEnsureSettled();
      }, 100);
    } else {
      // While in a chat and messages update: smooth scroll (current behavior)
      setTimeout(() => {
        scrollChatToBottom();
      }, 100);
    }
  }, [chatSessions, selectedChat]);

  // After loading skeleton finishes, ensure we land at the bottom
  useEffect(() => {
    if (!selectedChat) return;
    if (loadingChat) return;
    // small delay to allow DOM to render messages before measuring
    const id = setTimeout(() => {
      scrollChatToBottomEnsureSettled();
    }, 100);
    return () => clearTimeout(id);
  }, [loadingChat, selectedChat]);

  const scrollChatToBottom = () => {
    if (messagesContainerRef.current) {
      const scrollHeight = messagesContainerRef.current.scrollHeight;
      const currentScrollTop = messagesContainerRef.current.scrollTop;
      const maxScrollTop = scrollHeight - messagesContainerRef.current.clientHeight;
      const scrollTo = Math.min(currentScrollTop + (scrollHeight - currentScrollTop), maxScrollTop);
      messagesContainerRef.current.scrollTo({ top: scrollTo, behavior: "smooth" });
    }
  };

  const scrollChatToBottomImmediate = () => {
    if (messagesContainerRef.current) {
      const maxScrollTop = messagesContainerRef.current.scrollHeight - messagesContainerRef.current.clientHeight;
      messagesContainerRef.current.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
    }
  };

  // Ensures we end up truly at the bottom even if content grows (e.g., images render) after initial paint
  const scrollChatToBottomEnsureSettled = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    let lastHeight = -1;
    let stableFrames = 0;
    const start = performance.now();

    const tick = () => {
      const h = el.scrollHeight;
      if (h === lastHeight) stableFrames += 1;
      else stableFrames = 0;
      lastHeight = h;
      // Snap to bottom each frame
      el.scrollTop = Math.max(0, h - el.clientHeight);
      // Stop when height is stable for a couple frames or timeout
      if (stableFrames >= 2 || performance.now() - start > 800) return;
      requestAnimationFrame(tick);
    };

    // Start on next frame to allow DOM to paint
    requestAnimationFrame(tick);
  };

  // Handle scroll to update floating date indicator
  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container || !activeChat?.messages?.length) return;

    // Clear existing timeout
    if (floatingDateTimeoutRef.current) {
      clearTimeout(floatingDateTimeoutRef.current);
    }

    // Find the first visible message
    const containerRect = container.getBoundingClientRect();
    const messageElements = container.querySelectorAll("[data-message-timestamp]");

    let firstVisibleTimestamp = null;
    for (const el of messageElements) {
      const rect = el.getBoundingClientRect();
      // Check if message is visible in viewport (at least partially)
      if (rect.top >= containerRect.top && rect.top <= containerRect.bottom) {
        firstVisibleTimestamp = parseInt(el.getAttribute("data-message-timestamp"), 10);
        break;
      }
    }

    if (firstVisibleTimestamp) {
      const date = formatDateSeparator(firstVisibleTimestamp);
      setFloatingDate(date);
      setShowFloatingDate(true);

      // Hide after 2 seconds of no scrolling
      floatingDateTimeoutRef.current = setTimeout(() => {
        setShowFloatingDate(false);
      }, 2000);
    }
  };

  // Cleanup floating date timeout on unmount
  useEffect(() => {
    return () => {
      if (floatingDateTimeoutRef.current) {
        clearTimeout(floatingDateTimeoutRef.current);
      }
    };
  }, []);

  // Helper function to check if two timestamps are on different days
  const isDifferentDay = (timestamp1, timestamp2) => {
    const date1 = dayjs.unix(timestamp1).tz("Europe/Amsterdam").format("YYYY-MM-DD");
    const date2 = dayjs.unix(timestamp2).tz("Europe/Amsterdam").format("YYYY-MM-DD");
    return date1 !== date2;
  };

  // Helper function to format date separator
  const formatDateSeparator = (timestamp) => {
    const date = dayjs.unix(timestamp).tz("Europe/Amsterdam");
    const today = dayjs().tz("Europe/Amsterdam");
    const yesterday = today.subtract(1, "day");

    if (date.format("YYYY-MM-DD") === today.format("YYYY-MM-DD")) {
      return t("today");
    } else if (date.format("YYYY-MM-DD") === yesterday.format("YYYY-MM-DD")) {
      return t("yesterday");
    } else {
      return date.format("DD MMMM YYYY");
    }
  };

  // Helper function to format time/date for chat list
  const formatChatListTime = (timestamp) => {
    const date = dayjs.unix(timestamp).tz("Europe/Amsterdam");
    const today = dayjs().tz("Europe/Amsterdam");
    const yesterday = today.subtract(1, "day");

    if (date.format("YYYY-MM-DD") === today.format("YYYY-MM-DD")) {
      // Today: show only time
      return date.format("HH:mm");
    } else if (date.format("YYYY-MM-DD") === yesterday.format("YYYY-MM-DD")) {
      // Yesterday: show "Gisteren"
      return t("yesterday");
    } else {
      // Older: show date
      return date.format("DD/MM/YY");
    }
  };

  // Filter+sort chats for a given tab. Memoized per tab so switching tabs or typing in the
  // search box doesn't re-filter/re-sort all three tabs' worth of chats on every render.
  const filterChatsForTab = useCallback((tab, sessions, term) => {
    let filtered = sessions || [];

    if (tab === "unread") {
      filtered = filtered.filter((chat) => chat.unreadMessageCount > 0);
    } else if (tab === "unchecked") {
      filtered = filtered.filter((chat) => chat.needHuman === true);
    }

    if (term) {
      const lowerTerm = term.toLowerCase();
      filtered = filtered.filter(
        (chat) =>
          chat.contactName.toLowerCase().includes(lowerTerm) ||
          chat.recipientPhone.toString().toLowerCase().includes(lowerTerm),
      );
    }

    return [...filtered].sort((a, b) => b.updatedAt - a.updatedAt);
  }, []);

  const filteredChatsAll = useMemo(
    () => filterChatsForTab("all", chatSessions, searchTerm),
    [filterChatsForTab, chatSessions, searchTerm],
  );
  const filteredChatsUnread = useMemo(
    () => filterChatsForTab("unread", chatSessions, searchTerm),
    [filterChatsForTab, chatSessions, searchTerm],
  );
  const filteredChatsUnchecked = useMemo(
    () => filterChatsForTab("unchecked", chatSessions, searchTerm),
    [filterChatsForTab, chatSessions, searchTerm],
  );

  const filteredChatsByTab = useMemo(
    () => ({
      all: filteredChatsAll,
      unread: filteredChatsUnread,
      unchecked: filteredChatsUnchecked,
    }),
    [filteredChatsAll, filteredChatsUnread, filteredChatsUnchecked],
  );

  // Used by keyboard navigation (Tab key) to cycle through the currently active tab's list
  const getFilteredChats = useCallback(() => filteredChatsByTab[activeTab] || [], [filteredChatsByTab, activeTab]);

  const handleChatSelect = async (chat) => {
    // Reset synchronously (in the same batch as setSelectedChat) so the very first
    // useChatMessages call for this chat already uses the default limit, instead of
    // briefly querying with a stale (possibly much larger) limit left over from the
    // previous chat and then tearing that listener down again once the effect catches up.
    setMessageLimit(INITIAL_MESSAGE_LIMIT);
    setSelectedChat(chat);
    // Mark as read when opened and clear needHuman flag
    const updates = { unreadMessageCount: 0 };
    if (chat.needHuman === true) {
      updates.needHuman = false;
    }
    await updateChatSession(chat.id, updates);
    // Mobile: push history state to allow back gesture returning to list
    if (isMobile) {
      try {
        window.history.pushState({ chatId: chat.id }, "", window.location.href);
      } catch (_) {}
    }
  };

  const handleSendMessage = async () => {
    if (!activeChat) return;
    if (sendingMessageRef.current) return;

    const currentMode = activeChat.mode || "auto";
    if (currentMode === "auto") return;

    // If an attachment is staged, upload and send media first
    if (pendingAttachment?.file) {
      const isImage = pendingAttachment.type === "image";
      sendingMessageRef.current = true;
      setSendingMessage(true);
      try {
        await handleSendMedia(pendingAttachment.file, isImage ? "image" : "document", {
          caption: messageInput.trim(),
          clearCaption: !!messageInput.trim(),
        });
        clearPendingAttachment();
      } finally {
        sendingMessageRef.current = false;
        setSendingMessage(false);
      }
      return;
    }

    if (!messageInput.trim()) return;

    sendingMessageRef.current = true;
    setSendingMessage(true);

    try {
      const messageNumber = activeChat.messages.length + 1;

      const timestamp = dayjs().unix();
      const newMessage = {
        createdAt: timestamp,
        type: "outgoing",
        messageBody: messageInput,
        messageType: "text",
        senderId: uid,
        messageNumber,
        timestamps: {
          whatsappSent: null,
          whatsappDelivered: null,
          whatsappRead: null,
        },
      };

      // Save message to database
      const messageId = await saveOutgoingMessage(activeChat.id, newMessage);

      // Update chat session
      await updateChatSession(activeChat.id, {
        updatedAt: timestamp,
        messageCount: activeChat.messageCount + 1,
      });

      setMessageInput("");

      scrollChatToBottom();

      await sendWhatsappMessage(newMessage, activeChat.id, messageId);
    } finally {
      sendingMessageRef.current = false;
      setSendingMessage(false);
    }
  };

  const handleOpenScheduleModal = () => {
    if (!activeChat) return;
    const defaultScheduled = dayjs().tz("Europe/Amsterdam").add(5, "minute");
    setScheduledFor(defaultScheduled);
    setScheduleModalOpen(true);
  };

  const handleScheduleMessage = async () => {
    if (!activeChat) return;

    if (!messageInput.trim() && !pendingAttachment?.file) return;
    if (!scheduledFor) {
      messageApi.error(t("select_date_time") || "Selecteer datum en tijd");
      return;
    }

    const now = dayjs().tz("Europe/Amsterdam");
    const scheduledUnix = dayjs(scheduledFor).tz("Europe/Amsterdam").unix();
    const nowUnix = now.unix();

    if (scheduledUnix <= nowUnix) {
      messageApi.error(t("scheduled_time_must_be_future"));
      return;
    }

    const timestamp = dayjs().unix();
    const messageNumber = activeChat.messages.length + 1;

    try {
      setSchedulingMessage(true);

      const caption = (messageInput || "").trim();

      let newMessage;
      if (pendingAttachment?.file) {
        setUploadingMedia(true);
        const isImage = pendingAttachment.type === "image";
        const { url } = await uploadFileToStorage(pendingAttachment.file, activeChat.id);

        newMessage = {
          createdAt: timestamp,
          type: "outgoing",
          messageBody: caption,
          messageType: isImage ? "image" : "document",
          senderId: uid,
          messageNumber,
          fileDownloadUrl: url,
          fileName: pendingAttachment.file?.name,
          scheduledAt: scheduledUnix,
          timestamps: {
            whatsappSent: null,
            whatsappDelivered: null,
            whatsappRead: null,
          },
        };
      } else {
        newMessage = {
          createdAt: timestamp,
          type: "outgoing",
          messageBody: messageInput,
          messageType: "text",
          senderId: uid,
          messageNumber,
          scheduledAt: scheduledUnix,
          timestamps: {
            whatsappSent: null,
            whatsappDelivered: null,
            whatsappRead: null,
          },
        };
      }

      const messageId = await saveOutgoingMessage(activeChat.id, newMessage);

      await updateChatSession(activeChat.id, {
        updatedAt: timestamp,
        messageCount: activeChat.messageCount + 1,
      });

      const queueDocId = await enqueueOutgoingWhatsappMessage({
        chatId: activeChat.id,
        messageId,
        message: newMessage,
        scheduledAt: scheduledUnix,
      });

      try {
        await updateChatMessage(activeChat.id, messageId, { queueDocId, isCancelled: false });
      } catch (_) {
        // no-op (message still exists, cancellation can fallback to query by chatId+messageId)
      }

      setMessageInput("");
      clearPendingAttachment();
      setScheduleModalOpen(false);
      scrollChatToBottom();
    } catch (e) {
      messageApi.error(e?.message || t("error"));
    } finally {
      setUploadingMedia(false);
      setSchedulingMessage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleToggleMode = async () => {
    if (!activeChat || !uid) return;

    const currentMode = activeChat.mode || "auto";
    const nextMode = currentMode === "manual" ? "auto" : "manual";
    const now = dayjs().unix();

    try {
      const updateData = {
        mode: nextMode,
      };

      if (nextMode === "manual") {
        // Switching to manual mode - admin takes over
        updateData.takenOverBy = uid;
        updateData.takenOverAt = now;
      } else {
        // Switching back to auto mode - admin restores auto replies
        updateData.restoredBy = uid;
        updateData.restoredAt = now;
      }

      await updateChatSession(activeChat.id, updateData);
      messageApi.success(
        nextMode === "manual"
          ? t("notifications.chat_mode_switched_to_manual")
          : t("notifications.chat_mode_switched_to_auto"),
      );
    } catch (e) {
      messageApi.error(e?.message || t("error"));
    }
  };

  const renderTemplatePreview = (template, valuesObj) => {
    if (!template) return "";
    let text = template.body;
    template.variables.forEach((v) => {
      const val = valuesObj?.[v.key] ?? "";
      text = text.replace(new RegExp(`{{\\s*${v.key}\\s*}}`, "g"), val);
    });
    return text;
  };

  const handleOpenTemplateModal = () => {
    setSelectedTemplateId(null);
    setTemplateSearchTerm("");
    templateForm.resetFields();
    setTemplateModalOpen(true);
  };

  // New chat: open modal
  const handleOpenNewChatModal = () => {
    newChatForm.resetFields();
    setNewChatModalOpen(true);
  };

  // New chat: create in Firestore and select it
  const handleCreateNewChat = async () => {
    try {
      const values = await newChatForm.validateFields();
      const contactName = values.contactName.trim();
      const recipientPhone = values.recipientPhone.trim();
      const now = dayjs().unix();

      const id = await createChatSession({ contactName, recipientPhone });

      // Optimistically select the new chat
      setSelectedChat({
        id,
        contactName,
        recipientPhone,
        messages: [],
        unreadMessageCount: 0,
        messageCount: 0,
        updatedAt: now,
        mode: "manual",
        takenOverBy: null,
        takenOverAt: null,
        restoredBy: null,
        restoredAt: null,
      });

      setNewChatModalOpen(false);
      setSearchTerm("");
      messageApi.success(t("chat_created"));
    } catch (e) {
      if (e?.errorFields) return; // validation errors already shown
      messageApi.error(e?.message || t("error"));
    }
  };

  const handleSendTemplate = async () => {
    if (!activeChat) return;
    const template = whatsappTemplates.find((t) => t.id === selectedTemplateId);
    if (!template) {
      messageApi.error(t("select_template"));
      return;
    }
    let valuesObj = {};
    try {
      valuesObj = await templateForm.validateFields();
    } catch (_) {
      return;
    }
    const variablesArray = template.variables.map((v) => valuesObj[v.key]);

    const messageNumber = activeChat.messages.length + 1;
    const timestamp = dayjs().unix();
    const renderedBody = renderTemplatePreview(template, valuesObj);

    const newMessage = {
      createdAt: timestamp,
      type: "outgoing",
      messageBody: renderedBody,
      messageType: "template",
      senderId: uid,
      messageNumber,
      template: {
        id: template.id,
        name: template.name,
        language: template.language,
        variables: valuesObj,
        variablesArray,
      },
      timestamps: {
        whatsappSent: null,
        whatsappDelivered: null,
        whatsappRead: null,
      },
    };

    // Save message to database
    const messageId = await saveOutgoingMessage(activeChat.id, newMessage);

    // Update chat session
    await updateChatSession(activeChat.id, {
      updatedAt: timestamp,
      messageCount: activeChat.messageCount + 1,
    });

    setTemplateModalOpen(false);
    scrollChatToBottom();

    // Send via API
    await sendWhatsappMessage(newMessage, activeChat.id, messageId);
  };

  const sendWhatsappMessage = async (message, chatId, messageId) => {
    setSendingWhatsappMessageId(messageId);
    axios
      .post(
        process.env.REACT_APP_N8N_API_URL + "/webhook/whatsapp/chefs-farm/outgoing",
        { message, chatId, messageId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: process.env.REACT_APP_N8N_API_KEY,
          },
        },
      )
      .then(() => {
        setSendingWhatsappMessageId(null);
      })
      .catch((err) => {
        setSendingWhatsappMessageId(null);
        console.log(err);
        messageApi.error(err.message);
      });
  };

  const handleSendReaction = async (messageId, emoji) => {
    if (!activeChat) return;

    try {
      setSendingReactionMessageId(messageId);

      const message = activeChat.messages.find((m) => m.id === messageId);
      if (!message) return;

      // Check if message has wamid (required for reactions)
      if (!message.wamid) {
        messageApi.error("Cannot react to this message (no WhatsApp ID)");
        return;
      }

      // Check if removing reaction (same emoji clicked)
      const isRemoving = message.reaction?.emoji === emoji;

      const payload = {
        messageId,
        chatId: activeChat.id,
        message: {
          messageType: "reaction",
          emoji: isRemoving ? "" : emoji, // Empty string to remove
          targetMessageWamid: message.wamid, // The message we're reacting to
        },
      };

      await axios.post(process.env.REACT_APP_N8N_API_URL + "/webhook/whatsapp/chefs-farm/outgoing", payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_N8N_API_KEY,
        },
      });

      setReactionPickerMessageId(null);
    } catch (err) {
      console.error(err);
      messageApi.error(err.message || "Failed to send reaction");
    } finally {
      setSendingReactionMessageId(null);
    }
  };

  const getModeText = (mode) => {
    return mode === "manual" ? t("manual") || "Handmatig" : t("automatic") || "Automatisch";
  };

  // Index messages by wamid once per message-set change, so reply-reference lookups are O(1)
  // instead of an O(n) scan per message (which was O(n^2) for the whole chat on every render).
  const messagesByWamid = useMemo(() => {
    const map = new Map();
    (activeChat?.messages || []).forEach((m) => {
      if (m.wamid) map.set(m.wamid, m);
    });
    return map;
  }, [activeChat?.messages]);

  const handleDeleteChat = useCallback(
    async (chat) => {
      try {
        await deleteChatSession(chat.id);
        if (selectedChat?.id === chat.id) {
          setSelectedChat(null);
        }
        messageApi.success(t("chat_deleted"));
      } catch (e) {
        messageApi.error(e?.message || t("error"));
      }
    },
    [deleteChatSession, selectedChat?.id, messageApi, t],
  );

  const renderChatList = (tab) => {
    const filteredChats = filteredChatsByTab[tab] || [];

    return (
      <ChatListContainer>
        {/* Toolbar: search + new chat */}
        <div
          style={{
            padding: "12px 12px 8px 12px",
            borderBottom: "1px solid #f0f0f0",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t("search_chats")}
            value={searchInput}
            onChange={handleSearchInputChange}
            style={{ fontSize: isMobile ? "16px" : "14px" }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenNewChatModal}>
            {t("new_chat")}
          </Button>
        </div>
        {chatSessionsLoading ? (
          <ChatListSkeleton />
        ) : filteredChats.length === 0 ? (
          <Empty
            description={tab === "unread" ? t("no_unread_messages") : t("no_conversations_found")}
            style={{ marginTop: 40 }}
          />
        ) : (
          <VirtualizedChatList
            chats={filteredChats}
            selectedChatId={selectedChat?.id}
            formatChatListTime={formatChatListTime}
            t={t}
            onSelect={handleChatSelect}
            onDelete={handleDeleteChat}
          />
        )}
      </ChatListContainer>
    );
  };

  const renderChatDetail = () => {
    if (!activeChat) {
      return (
        <EmptyChat>
          <MessageOutlined style={{ fontSize: "64px", marginBottom: "16px" }} />
          <Title level={4}>{t("select_chat_title")}</Title>
          <Text>{t("select_chat_description")}</Text>
        </EmptyChat>
      );
    }

    const currentMode = activeChat.mode || "auto";
    const isAutoMode = currentMode === "auto";

    if (autoModeSendWarning && !isAutoMode) {
      setAutoModeSendWarning(false);
    }

    return (
      <ChatDetailContainer>
        {loadingChat && (
          <LoadingOverlay>
            <div
              style={{
                padding: "16px 24px",
                borderBottom: "1px solid #e8e8e8",
                background: "#ededed",
                width: "100%",
              }}
            >
              <Skeleton active avatar title paragraph={{ rows: 1, width: "60%" }} />
            </div>
            <div style={{ padding: 24, width: "100%" }}>
              <Skeleton active paragraph={{ rows: 8 }} />
            </div>
          </LoadingOverlay>
        )}
        {/* Header */}
        <div
          style={{
            padding: isMobile ? "10px 16px" : "16px 24px",
            borderBottom: "1px solid #e8e8e8",
            background: "#ededed",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTopRightRadius: "8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "8px" : "12px", flex: 1, minWidth: 0 }}>
            {isMobile && (
              <Button
                icon={<LeftOutlined />}
                type="text"
                onClick={() => {
                  try {
                    if (isMobile) window.history.back();
                  } catch (_) {
                    setSelectedChat(null);
                  }
                }}
                style={{ padding: "4px" }}
              />
            )}
            <Avatar
              src={activeChat.avatar}
              icon={<UserOutlined />}
              style={{ backgroundColor: "#1890ff" }}
              size={isMobile ? "default" : "large"}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              {editingName ? (
                <Input
                  size="small"
                  autoFocus
                  value={editingNameValue}
                  onChange={(e) => setEditingNameValue(e.target.value)}
                  onPressEnter={async () => {
                    const newName = (editingNameValue || "").trim();
                    if (!newName) {
                      messageApi.error(t("contact_name_required"));
                      return;
                    }
                    try {
                      await updateChatSession(activeChat.id, { contactName: newName });
                      setSelectedChat({ ...activeChat, contactName: newName });
                    } catch (e) {
                      messageApi.error(e?.message || t("error"));
                    } finally {
                      setEditingName(false);
                    }
                  }}
                  onBlur={async () => {
                    const newName = (editingNameValue || "").trim();
                    if (!newName || newName === activeChat.contactName) {
                      setEditingName(false);
                      return;
                    }
                    try {
                      await updateChatSession(activeChat.id, { contactName: newName });
                      setSelectedChat({ ...activeChat, contactName: newName });
                    } catch (e) {
                      messageApi.error(e?.message || t("error"));
                    } finally {
                      setEditingName(false);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setEditingName(false);
                      setEditingNameValue(activeChat.contactName || "");
                    }
                  }}
                  style={{
                    margin: 0,
                    padding: "0 6px",
                    height: 28,
                    fontSize: isMobile ? "16px" : "14px",
                  }}
                />
              ) : (
                <Title
                  level={5}
                  style={{
                    margin: 0,
                    color: "#111b21",
                    cursor: "pointer",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => {
                    setEditingName(true);
                    setEditingNameValue(activeChat.contactName || "");
                  }}
                  title={t("click_to_edit") || "Click to edit"}
                >
                  {activeChat.contactName}
                </Title>
              )}
              <Text
                type="secondary"
                style={{
                  fontSize: isMobile ? "12px" : "13px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                {activeChat.recipientPhone}
              </Text>
            </div>
          </div>

          {/* Mode badge and actions */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            {!isMobile && (
              <Badge>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "4px 8px" }}>
                  {currentMode === "manual" ? (
                    <UserOutlined style={{ fontSize: "12px" }} />
                  ) : (
                    <RobotOutlined style={{ fontSize: "12px" }} />
                  )}
                  <span style={{ fontSize: "12px" }}>{getModeText(currentMode)}</span>
                </div>
              </Badge>
            )}

            {isMobile ? (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "mode",
                      icon: currentMode === "manual" ? <RobotOutlined /> : <UserOutlined />,
                      label: currentMode === "manual" ? t("switch_to_auto") : t("switch_to_manual"),
                      onClick: handleToggleMode,
                    },
                    {
                      type: "divider",
                    },
                    {
                      key: "delete",
                      icon: <DeleteOutlined />,
                      label: t("delete_chat"),
                      danger: true,
                      onClick: () => {
                        Modal.confirm({
                          title: t("delete_chat_confirm_title"),
                          content: t("delete_chat_confirm_description"),
                          okText: t("delete"),
                          okType: "danger",
                          cancelText: t("cancel"),
                          onOk: async () => {
                            try {
                              await deleteChatSession(activeChat.id);
                              setSelectedChat(null);
                              messageApi.success(t("chat_deleted"));
                            } catch (e) {
                              messageApi.error(e?.message || t("error"));
                            }
                          },
                        });
                      },
                    },
                  ],
                }}
                trigger={["click"]}
              >
                <Button type="text" icon={<MoreOutlined />} style={{ padding: "4px 8px" }} />
              </Dropdown>
            ) : (
              <Button size="small" onClick={handleToggleMode}>
                {currentMode === "manual" ? t("switch_to_auto") : t("switch_to_manual")}
              </Button>
            )}
          </div>
        </div>

        {/* Invisible full-surface drag & drop overlay */}
        <StyledDragger
          $isDragging={dragActive}
          multiple={false}
          accept="image/*,*/*"
          beforeUpload={() => false}
          showUploadList={false}
          disabled={!activeChat || uploadingMedia}
          onChange={(info) => {
            const latest = Array.isArray(info.fileList) ? info.fileList[info.fileList.length - 1] : info.file;
            const origin = latest?.originFileObj;
            if (origin) setPendingFromFile(origin);
          }}
        >
          {dragActive && (
            <UploadOverlayMessage>
              <h2>{t("drag_and_drop_file_chat_title")}</h2>
              <p>{t("drag_and_drop_file_chat_message")}</p>
            </UploadOverlayMessage>
          )}
        </StyledDragger>
        {/* Messages */}
        <MessagesContainer ref={messagesContainerRef} onScroll={handleMessagesScroll}>
          {hasMoreMessages && !loadingChat && (
            <LoadMoreMessagesBar>
              <Button
                size="small"
                onClick={() => setMessageLimit((prev) => prev + INITIAL_MESSAGE_LIMIT)}
                loading={selectedChatMessagesLoading}
              >
                {t("load_earlier_messages") || "Eerdere berichten laden"}
              </Button>
            </LoadMoreMessagesBar>
          )}
          {/* Floating date indicator */}
          {floatingDate && <FloatingDateIndicator $visible={showFloatingDate}>{floatingDate}</FloatingDateIndicator>}
          {(activeChat.messages || []).map((message, index) => {
            const isOwn = message.type === "outgoing";
            const isIncoming = message.type === "incoming";
            const isSending = sendingWhatsappMessageId === message.id;
            const isScheduled =
              isOwn &&
              !!message.scheduledAt &&
              !message.isCancelled &&
              !message.timestamps?.whatsappSent &&
              dayjs.unix(message.scheduledAt).isAfter(dayjs());
            const messages = activeChat.messages || [];
            const prevMessage = index > 0 ? messages[index - 1] : null;
            const showDateSeparator = !prevMessage || isDifferentDay(prevMessage.createdAt, message.createdAt);
            const referencedMessage = message.contextWamid ? messagesByWamid.get(message.contextWamid) || null : null;

            return (
              <MessageItem
                key={message.id}
                message={{
                  ...message,
                  dateSeparatorLabel: showDateSeparator ? formatDateSeparator(message.createdAt) : null,
                }}
                isOwn={isOwn}
                isIncoming={isIncoming}
                isSending={isSending}
                isScheduled={isScheduled}
                showDateSeparator={showDateSeparator}
                referencedMessage={referencedMessage}
                isHovered={hoveredMessageId === message.id}
                isReactionPickerOpen={reactionPickerMessageId === message.id}
                reactionPickerPlacement={reactionPickerPlacement}
                sendingReactionMessageId={sendingReactionMessageId}
                messagesContainerRef={messagesContainerRef}
                t={t}
                onMouseEnter={() => setHoveredMessageId(message.id)}
                onMouseLeave={() => setHoveredMessageId(null)}
                onImageClick={() =>
                  setImagePreview({
                    open: true,
                    url: message.fileDownloadUrl,
                    caption: message.messageBody || "",
                  })
                }
                onFileDownload={handleFileDownload}
                onCancelScheduled={(msg) => handleCancelScheduledMessage(activeChat.id, msg)}
                onResend={async (msg) => {
                  try {
                    await sendWhatsappMessage(msg, activeChat.id, msg.id);
                  } catch (error) {
                    messageApi.error(error.message);
                  }
                }}
                onRemoveReaction={(msg) => handleSendReaction(msg.id, msg.reaction.emoji)}
                onOpenReactionPicker={(e, msg) => {
                  try {
                    const rect = e?.currentTarget?.getBoundingClientRect?.();
                    const pickerHeight = 400;
                    const gap = 8;
                    const containerRect = messagesContainerRef.current?.getBoundingClientRect?.();
                    const containerTop = containerRect?.top ?? 0;
                    const containerBottom = containerRect?.bottom ?? window.innerHeight;

                    const spaceAbove = (rect?.top ?? containerTop) - containerTop;
                    const spaceBelow = containerBottom - (rect?.bottom ?? containerBottom);
                    const shouldOpenDown = spaceAbove < pickerHeight + gap && spaceBelow > spaceAbove;
                    setReactionPickerPlacement(shouldOpenDown ? "bottom" : "top");
                  } catch (_) {
                    setReactionPickerPlacement("top");
                  }
                  setReactionPickerMessageId(msg.id);
                }}
                onSendReaction={handleSendReaction}
                reactionPickerRef={reactionPickerRef}
              />
            );
          })}
        </MessagesContainer>

        {/* Input */}
        <InputContainer>
          {/* Staged attachment preview */}
          {pendingAttachment && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: 8,
                border: "1px dashed #d9d9d9",
                borderRadius: 8,
                marginBottom: 8,
                background: "#fafafa",
              }}
            >
              {pendingAttachment.type === "image" ? (
                <img
                  src={pendingAttachment.previewUrl}
                  alt="preview"
                  style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6 }}
                />
              ) : (
                <FileOutlined style={{ fontSize: 24, color: "#1677ff" }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text ellipsis style={{ display: "block" }}>
                  {pendingAttachment.file?.name || (pendingAttachment.type === "image" ? t("image") : t("file"))}
                </Text>
                {pendingAttachment.type === "image" && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("press_send_to_send")}
                  </Text>
                )}
              </div>
              <Button size="small" onClick={clearPendingAttachment} icon={<CrossIcon size={10} color={"red"} />} />
            </div>
          )}
          {/* hidden inputs for media */}
          <input
            type="file"
            accept="image/*"
            ref={imageInputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (file) setPendingFromFile(file);
            }}
          />
          <input
            type="file"
            accept="*/*"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (file) setPendingFromFile(file);
            }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 4 : 8, width: "100%" }}>
            {isMobile ? (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "image",
                      icon: <PictureOutlined />,
                      label: t("send_image"),
                      onClick: () => imageInputRef.current && imageInputRef.current.click(),
                      disabled: uploadingMedia,
                    },
                    {
                      key: "document",
                      icon: <PaperClipOutlined />,
                      label: t("send_document"),
                      onClick: () => fileInputRef.current && fileInputRef.current.click(),
                      disabled: uploadingMedia,
                    },
                    {
                      type: "divider",
                    },
                    {
                      key: "template",
                      icon: <MessageOutlined />,
                      label: t("send_template"),
                      onClick: handleOpenTemplateModal,
                    },
                  ],
                }}
                trigger={["click"]}
                disabled={!activeChat}
              >
                <Button icon={<PlusOutlined />} disabled={!activeChat} />
              </Dropdown>
            ) : (
              <>
                <Button onClick={handleOpenTemplateModal} disabled={!activeChat}>
                  {t("send_template")}
                </Button>
                <Button
                  icon={<PictureOutlined />}
                  onClick={() => imageInputRef.current && imageInputRef.current.click()}
                  disabled={!activeChat || uploadingMedia}
                />
                <Button
                  icon={<PaperClipOutlined />}
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  disabled={!activeChat || uploadingMedia}
                />
              </>
            )}

            <Space.Compact style={{ width: "100%", position: "relative" }}>
              <Input.TextArea
                value={messageInput}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setMessageInput(nextValue);
                  if (autoModeSendWarning && !nextValue.trim()) {
                    setAutoModeSendWarning(false);
                  }
                }}
                placeholder={t("enter_a_message")}
                onPaste={handlePasteImage}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (isAutoMode) {
                      if (messageInput.trim() || pendingAttachment?.file) {
                        setAutoModeSendWarning(true);
                      }
                      return;
                    }
                    if (sendingMessage) return;

                    handleSendMessage();
                  }
                }}
                disabled={!activeChat}
                autoSize={{ minRows: 1, maxRows: 6 }}
                style={{
                  borderRadius: "21px",
                  padding: "8px 16px",
                  fontSize: isMobile ? "16px" : "15px",
                  resize: "none",
                  ...(autoModeSendWarning && isAutoMode
                    ? {
                        borderColor: "#ff4d4f",
                        boxShadow: "0 0 0 2px rgba(255, 77, 79, 0.2)",
                      }
                    : {}),
                }}
              />
              {autoModeSendWarning && isAutoMode && (
                <div style={{ position: "absolute", left: 0, bottom: -20 }}>
                  <Text type="danger">{t("switch_to_manual_to_send_messages")}</Text>
                </div>
              )}
              <div style={{ position: "relative" }} ref={emojiPickerRef}>
                <Button
                  icon={<SmileOutlined />}
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  disabled={!activeChat}
                  style={{
                    borderRadius: "50%",
                    width: isMobile ? "36px" : "42px",
                    height: isMobile ? "36px" : "42px",
                    marginLeft: isMobile ? "4px" : "8px",
                  }}
                />
                {showEmojiPicker && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "50px",
                      right: "0",
                      zIndex: 1000,
                    }}
                  >
                    <EmojiPicker
                      onEmojiClick={(emojiData) => {
                        setMessageInput((prev) => prev + emojiData.emoji);
                        setShowEmojiPicker(false);
                      }}
                      width={isMobile ? 300 : 350}
                      height={isMobile ? 400 : 550}
                    />
                  </div>
                )}
              </div>
              {!isMobile && (
                <Button
                  icon={<ClockCircleOutlined />}
                  onClick={handleOpenScheduleModal}
                  disabled={
                    !activeChat ||
                    uploadingMedia ||
                    schedulingMessage ||
                    (!messageInput.trim() && !pendingAttachment?.file)
                  }
                  style={{
                    borderRadius: "50%",
                    width: "42px",
                    height: "42px",
                    marginLeft: "8px",
                  }}
                />
              )}
              <Tooltip title={isAutoMode ? t("switch_to_manual_to_send_messages") : ""}>
                <span>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSendMessage}
                    disabled={
                      !activeChat ||
                      isAutoMode ||
                      uploadingMedia ||
                      sendingMessage ||
                      (!messageInput.trim() && !pendingAttachment)
                    }
                    loading={uploadingMedia || sendingMessage}
                    style={{
                      borderRadius: "50%",
                      width: isMobile ? "36px" : "42px",
                      height: isMobile ? "36px" : "42px",
                      marginLeft: isMobile ? "4px" : "8px",
                    }}
                  />
                </span>
              </Tooltip>
            </Space.Compact>
          </div>
        </InputContainer>
      </ChatDetailContainer>
    );
  };

  const unreadCount = chatSessions?.filter((chat) => chat.unreadMessageCount > 0).length;
  const uncheckedCount = chatSessions?.filter((chat) => chat.needHuman === true).length;

  // Desktop: show split view. Mobile: show list OR detail
  const renderContent = () => {
    if (isMobile) {
      return (
        <ChatContainer>
          {!selectedChat ? (
            <ChatListPanel style={{ width: "100%" }}>
              {/* Chat List Header */}
              <div
                style={{
                  padding: "16px",
                  borderBottom: "1px solid #e8e8e8",
                  background: "#ededed",
                  borderTopLeftRadius: "8px",
                }}
              >
                <Title level={4} style={{ margin: "0 0 16px 0", color: "#111b21" }}>
                  {t("chats")}
                </Title>
                <Input
                  placeholder={t("search_chats")}
                  prefix={<SearchOutlined />}
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  style={{ borderRadius: "21px", fontSize: "16px" }}
                />
              </div>
              {/* Tabs */}
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                destroyOnHidden={true}
                style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}
                tabBarStyle={{
                  margin: 0,
                  padding: "0 16px",
                  borderBottom: "1px solid #e8e8e8",
                }}
              >
                <TabPane
                  tab={
                    <Space>
                      {t("all_chats")}
                      <Badge count={chatSessions?.length} showZero style={{ backgroundColor: "#1890ff" }} />
                    </Space>
                  }
                  key="all"
                  style={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}
                >
                  {renderChatList("all")}
                </TabPane>
                <TabPane
                  tab={
                    <Space>
                      {t("unread")}
                      <Badge count={unreadCount} />
                    </Space>
                  }
                  key="unread"
                  style={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}
                >
                  {renderChatList("unread")}
                </TabPane>
                <TabPane
                  tab={
                    <Space>
                      {t("unchecked")}
                      <Badge count={uncheckedCount} style={{ backgroundColor: "#fa8c16" }} />
                    </Space>
                  }
                  key="unchecked"
                  style={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}
                >
                  {renderChatList("unchecked")}
                </TabPane>
              </Tabs>
            </ChatListPanel>
          ) : (
            <ChatDetailPanel>{renderChatDetail()}</ChatDetailPanel>
          )}
        </ChatContainer>
      );
    }
    // Desktop
    return (
      <ChatContainer>
        {contextHolder}
        <ChatListPanel>
          {/* Chat List Header */}
          <div
            style={{
              padding: "16px",
              borderBottom: "1px solid #e8e8e8",
              background: "#ededed",
              borderTopLeftRadius: "8px",
            }}
          >
            <Title level={4} style={{ margin: "0 0 16px 0", color: "#111b21" }}>
              {t("chats")}
            </Title>
            <Input
              placeholder={t("search_chats")}
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={handleSearchInputChange}
              style={{ borderRadius: "21px", fontSize: "16px" }}
            />
          </div>

          {/* Tabs */}
          <Tabs
            activeKey={activeTab}
            destroyOnHidden={true}
            onChange={setActiveTab}
            style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}
            tabBarStyle={{
              margin: 0,
              padding: "0 16px",
              borderBottom: "1px solid #e8e8e8",
            }}
          >
            <TabPane
              tab={
                <Space>
                  {t("all_chats")}
                  <Badge count={chatSessions?.length} showZero style={{ backgroundColor: "#1890ff" }} />
                </Space>
              }
              key="all"
              style={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}
            >
              {renderChatList("all")}
            </TabPane>
            <TabPane
              tab={
                <Space>
                  {t("unread")}
                  <Badge count={unreadCount} />
                </Space>
              }
              key="unread"
              style={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}
            >
              {renderChatList("unread")}
            </TabPane>
            <TabPane
              tab={
                <Space>
                  {t("unchecked")}
                  <Badge count={uncheckedCount} style={{ backgroundColor: "#fa8c16" }} />
                </Space>
              }
              key="unchecked"
              style={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}
            >
              {renderChatList("unchecked")}
            </TabPane>
          </Tabs>
        </ChatListPanel>

        <ChatDetailPanel>{renderChatDetail()}</ChatDetailPanel>
        <Modal
          open={imagePreview.open}
          footer={null}
          onCancel={() => setImagePreview({ open: false, url: "", caption: "" })}
          centered
          width={800}
        >
          <div style={{ textAlign: "center" }}>
            {imagePreview.url ? (
              <ChatImagePreview src={imagePreview.url} alt={imagePreview.caption || "image preview"} />
            ) : null}
            {imagePreview.caption ? (
              <div style={{ marginTop: 12, color: "#303030" }}>
                {Utils.parseWhatsAppFormatting(imagePreview.caption)}
              </div>
            ) : null}
          </div>
        </Modal>
        <Modal
          open={scheduleModalOpen}
          onCancel={() => setScheduleModalOpen(false)}
          onOk={handleScheduleMessage}
          okText={t("schedule") || "Inplannen"}
          cancelText={t("cancel")}
          confirmLoading={schedulingMessage}
          title={t("schedule_message") || "Bericht inplannen"}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <DatePicker
              showTime={{ format: "HH:mm", minuteStep: 1, showSecond: false }}
              value={scheduledFor}
              onChange={(val) => {
                if (!val) {
                  setScheduledFor(null);
                  return;
                }
                setScheduledFor(dayjs(val).second(0));
              }}
              format="DD-MM-YYYY HH:mm"
              style={{ width: "100%" }}
            />
          </div>
        </Modal>
      </ChatContainer>
    );
  };

  return (
    <>
      {contextHolder}
      {renderContent()}

      {/* New Chat Modal */}
      <Modal
        open={newChatModalOpen}
        title={t("new_chat")}
        okText={t("create")}
        cancelText={t("cancel")}
        onOk={handleCreateNewChat}
        onCancel={() => setNewChatModalOpen(false)}
      >
        <Form form={newChatForm} layout="vertical">
          <Form.Item
            name="recipientPhone"
            label={t("phone_number")}
            rules={[
              { required: true, message: t("phone_number_required") },
              {
                pattern: /^\d{11}$/,
                message: t("phone_number_format_hint"),
              },
            ]}
          >
            <Input placeholder="31612345678" maxLength={15} style={{ fontSize: "16px" }} />
          </Form.Item>
          <Form.Item
            name="contactName"
            label={t("contact_name")}
            rules={[{ required: true, message: t("contact_name_required") }]}
          >
            <Input placeholder={t("contact_name_placeholder")} style={{ fontSize: "16px" }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Template Sender Modal */}
      <Modal
        open={templateModalOpen}
        onCancel={() => {
          setTemplateModalOpen(false);
          setSelectedTemplateId(null);
        }}
        onOk={handleSendTemplate}
        okText={t("send")}
        cancelText={t("cancel")}
        title={t("send_whatsapp_template")}
        width={isMobile ? "100%" : 600}
        style={isMobile ? { top: 0, maxWidth: "100%", margin: 0, paddingBottom: 0 } : {}}
        bodyStyle={isMobile ? { maxHeight: "calc(100vh - 110px)", overflowY: "auto" } : {}}
      >
        {isMobile ? (
          /* Mobile: Show template list first, then variables */
          !selectedTemplateId ? (
            <div>
              <Input
                placeholder={t("search_templates")}
                prefix={<SearchOutlined />}
                value={templateSearchTerm}
                onChange={(e) => setTemplateSearchTerm(e.target.value)}
                style={{ marginBottom: 16, fontSize: "16px" }}
              />
              <List
                dataSource={whatsappTemplates.filter((tpl) => {
                  if (!templateSearchTerm) return true;
                  const search = templateSearchTerm.toLowerCase();
                  return (
                    tpl.id.toLowerCase().includes(search) ||
                    tpl.name.toLowerCase().includes(search) ||
                    tpl.body.toLowerCase().includes(search)
                  );
                })}
                renderItem={(tpl) => (
                  <List.Item
                    onClick={() => {
                      setSelectedTemplateId(tpl.id);
                      templateForm.setFieldsValue(Object.fromEntries(tpl.variables.map((v) => [v.key, undefined])));
                    }}
                    style={{
                      cursor: "pointer",
                      padding: "12px",
                      borderRadius: 8,
                      marginBottom: 8,
                      background: "#fafafa",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <List.Item.Meta
                      title={<Text strong>{tpl.name}</Text>}
                      description={
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            ID: {tpl.id}
                          </Text>
                          <div style={{ marginTop: 4, fontSize: 13, color: "#666" }}>
                            {tpl.body.length > 100 ? `${tpl.body.substring(0, 100)}...` : tpl.body}
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          ) : (
            <div>
              <Button
                icon={<LeftOutlined />}
                type="text"
                onClick={() => setSelectedTemplateId(null)}
                style={{ marginBottom: 16 }}
              >
                {t("back")}
              </Button>
              <Form form={templateForm} layout="vertical">
                {(() => {
                  const tpl = whatsappTemplates.find((t) => t.id === selectedTemplateId);
                  if (!tpl) return null;
                  return (
                    <>
                      <div
                        style={{
                          padding: "12px",
                          background: "#e6f7ff",
                          border: "1px solid #91d5ff",
                          borderRadius: 6,
                          marginBottom: 16,
                        }}
                      >
                        <Text strong>{tpl.name}</Text>
                        <div style={{ marginTop: 4, fontSize: 13, color: "#666" }}>{tpl.body}</div>
                      </div>
                      {tpl.variables.map((v) => (
                        <Form.Item key={v.key} name={v.key} label={v.label} rules={[{ required: true }]}>
                          <Input placeholder={v.placeholder} style={{ fontSize: "16px" }} />
                        </Form.Item>
                      ))}
                      <div
                        style={{ padding: "12px", background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 6 }}
                      >
                        <Text strong>{t("preview")}:</Text>
                        <div style={{ marginTop: 8, whiteSpace: "pre-line" }}>
                          {renderTemplatePreview(tpl, watchedTemplateValues || {})}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </Form>
            </div>
          )
        ) : (
          /* Desktop: Keep original Select dropdown */
          <Form form={templateForm} layout="vertical">
            <Form.Item label={t("template")} required>
              <Select
                showSearch
                placeholder={t("select_template")}
                value={selectedTemplateId}
                onChange={(val) => {
                  setSelectedTemplateId(val);
                  // reset only dynamic fields
                  templateForm.setFieldsValue(
                    Object.fromEntries(
                      (whatsappTemplates.find((t) => t.id === val)?.variables || []).map((v) => [v.key, undefined]),
                    ),
                  );
                }}
                filterOption={(input, option) => {
                  const tpl = whatsappTemplates.find((t) => t.id === option.value);
                  const searchStr = `${tpl?.id || ""} ${tpl?.name || ""} ${tpl?.body || ""}`.toLowerCase();
                  return searchStr.includes(input.toLowerCase());
                }}
                options={whatsappTemplates.map((t) => ({ value: t.id, label: `${t.id} - ${t.name}` }))}
              />
            </Form.Item>

            {(() => {
              const tpl = whatsappTemplates.find((t) => t.id === selectedTemplateId);
              if (!tpl) return null;
              return (
                <>
                  {tpl.variables.map((v) => (
                    <Form.Item key={v.key} name={v.key} label={v.label} rules={[{ required: true }]}>
                      <Input placeholder={v.placeholder} style={{ fontSize: "16px" }} />
                    </Form.Item>
                  ))}
                  <div style={{ padding: "12px", background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 6 }}>
                    <Text strong>{t("preview")}:</Text>
                    <div style={{ marginTop: 8, whiteSpace: "pre-line" }}>
                      {renderTemplatePreview(tpl, watchedTemplateValues || {})}
                    </div>
                  </div>
                </>
              );
            })()}
          </Form>
        )}
      </Modal>
    </>
  );
};

const UploadOverlayMessage = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(2px);
  z-index: 2;
  pointer-events: none;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease-in-out;
`;

const StyledDragger = styled(Dragger)`
  position: absolute !important;
  inset: 0 !important;
  z-index: 5;
  background: transparent !important;
  pointer-events: ${(props) => (props.$isDragging ? "auto" : "none")};

  .ant-upload-drag {
    cursor: default !important;
    border: none !important;
    border: ${(props) => (props.$isDragging ? "2px dashed #40a9ff" : "none")} !important;
    background: ${(props) => (props.$isDragging ? "#f0f8ff" : "transparent")} !important;
    transition: all 0.2s ease-in-out;
    height: 100% !important;
    width: 100% !important;

    .ant-upload {
      padding: 0 !important;
      cursor: default !important;
      height: 100% !important;
      width: 100% !important;
    }

    &:hover {
      border: ${(props) => (props.$isDragging ? "2px dashed #40a9ff" : "none")} !important;
      background: ${(props) => (props.$isDragging ? "#f0f8ff" : "transparent")} !important;
    }
  }
`;

const ChatContainer = styled.div`
  height: calc(-174px + 100vh);
  max-height: 100vh;
  display: flex;
  background: #f0f2f5;
  border-radius: 8px;
  overflow: hidden;
`;

const ChatListPanel = styled.div`
  width: 400px;
  background: white;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  height: 100%;
  border-bottom-left-radius: 8px;

  .ant-tabs {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .ant-tabs-content-holder {
    flex: 1;
    overflow: hidden;
  }

  .ant-tabs-content {
    height: 100%;
  }

  .ant-tabs-tabpane {
    height: 100%;
  }
`;

const ChatDetailPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const ChatListContainer = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .ant-tabs-content {
    height: 100%;
  }

  .ant-tabs-tabpane {
    height: 100%;
  }
`;

const ChatDetailContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #efeae2;
  overflow-x: hidden;
  position: relative;
`;

const LoadMoreMessagesBar = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px 0;
  background: #efeae2;
`;

const FloatingDateIndicator = styled.div`
  position: sticky;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  background: rgba(255, 255, 255, 0.95);
  padding: 6px 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  font-size: 13px;
  color: #667781;
  font-weight: 500;
  width: fit-content;
  margin: 0 auto 12px auto;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;
  pointer-events: none;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: #ffffff;
  z-index: 10;
  display: flex;
  flex-direction: column;
  pointer-events: auto;
`;

const ChatImagePreview = styled.img`
  display: inline-block;
  max-width: 100%;
  max-height: 70vh;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
`;

const InputContainer = styled.div`
  padding: 16px;
  background: #f0f0f0;
  border-top: 1px solid #e8e8e8;
  border-bottom-right-radius: 8px;

  ${media.mobile`
    padding: 8px;
  `}
`;

const EmptyChat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #667781;

  .ant-typography {
    color: #667781;
  }
`;

export default Chat;
